import { AlertCircle, Loader2, LogIn, ShieldCheck } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '@/api/client';
import EarningsDisclaimer from '@/components/EarningsDisclaimer';
import GoogleTranslate from '@/components/GoogleTranslate';
import TurnstileWidget from '@/components/TurnstileWidget';
import { useAuth } from '@/contexts/AuthContext';
import { safeNextPath } from '@/lib/safeNext';

const LoginPage = () => {
  const { login, authUser, bootLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // `?next=` (set by the auth guard) brings the member back to the page they asked for.
  const nextPath = safeNextPath(new URLSearchParams(location.search).get('next'));
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [captchaSiteKey, setCaptchaSiteKey] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaRequired, setCaptchaRequired] = useState(false);

  useEffect(() => {
    let alive = true;
    api
      .get('/api/auth/captcha-config')
      .then((r) => {
        if (alive && r?.data?.configured) setCaptchaSiteKey(r.data.site_key || '');
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (!bootLoading && authUser) {
      navigate(nextPath, { replace: true });
    }
  }, [bootLoading, authUser, navigate, nextPath]);

  const onCaptchaVerified = useCallback((token) => {
    setCaptchaToken(token || '');
  }, []);

  if (bootLoading) {
    return (
      <div className="sc-canvas min-h-screen flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-[#FFB612]" />
      </div>
    );
  }

  if (authUser) return <Navigate to={nextPath} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!identifier.trim() || !password) {
      setErr('Enter your username/email and password.');
      return;
    }
    if (captchaRequired && captchaSiteKey && !captchaToken) {
      setErr('Please complete the human-verification challenge below.');
      return;
    }
    setBusy(true);
    try {
      await login(identifier.trim(), password, captchaToken || undefined);
      toast.success('Signed in');
      navigate(nextPath, { replace: true });
    } catch (e2) {
      const status = e2?.response?.status;
      const data = e2?.response?.data;
      const detail = data?.detail;
      const mwError = data?.error;
      if (typeof detail === 'object' && detail !== null && detail?.code === 'CAPTCHA_REQUIRED') {
        if (detail?.captcha_site_key) setCaptchaSiteKey(detail.captcha_site_key);
        setCaptchaRequired(true);
        setErr(detail?.message || 'Please complete the verification below.');
      } else {
        // Extract human-readable message from BOTH error envelopes the
        // backend may produce:
        //   - FastAPI default:           {"detail": "..."}  or  {"detail": {"code","message"}}
        //   - Security middleware (503): {"error":  {"code","message"}}
        // Without checking BOTH, a 503 from event-loop lag / conn-cap /
        // UAM gate would fall through to the generic Network-Error path.
        let msg;
        if (typeof detail === 'object' && detail !== null) msg = detail?.message || detail?.code;
        else if (typeof detail === 'string') msg = detail;
        else if (mwError && typeof mwError === 'object') msg = mwError?.message || mwError?.code;
        else if (e2?.message?.includes('Network Error')) msg = "Network error — couldn't reach the server.";
        else msg = 'Sign-in failed';
        if (status && !msg.includes('HTTP')) {
          msg = `${msg} (HTTP ${status})`;
        }
        setErr(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  const refQS = new URLSearchParams(location.search).get('ref');

  return (
    <main className="sc-canvas min-h-screen relative" data-testid="login-page">
      <header className="absolute top-0 left-0 right-0 z-30 px-5 md:px-8 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="login-brand-home">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-gradient-to-br from-[#FFC20E] to-[#FFB612]">
            <ShieldCheck className="w-4 h-4 text-[#0a0a0b]" strokeWidth={2.6} />
          </div>
          <div className="font-display font-bold text-sm whitespace-nowrap">
            1Simple<span className="sc-gold-text">Cycler</span>
            <span className="text-[var(--sc-text-mute)] text-[11px] ml-1">3.0</span>
          </div>
        </Link>
        <GoogleTranslate />
      </header>

      <div className="flex items-center justify-center min-h-screen px-5 py-24">
        <form onSubmit={submit} className="sc-glass-strong rounded-2xl w-full max-w-md p-6 md:p-8 space-y-5 sc-fade-in" data-testid="login-form">
          <div className="text-center">
            <div className="sc-pill mx-auto">
              <span className="sc-dot" />
              Member Sign-In
            </div>
            <h1 className="font-display font-bold text-2xl md:text-3xl mt-3">Welcome back</h1>
            <p className="text-sm text-[var(--sc-text-dim)] mt-1">Sign in to continue to your dashboard.</p>
          </div>

          {err ? (
            <div
              className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-[rgba(255,105,97,0.08)] border border-[rgba(255,105,97,0.32)] text-[#FF8A82] text-[13px]"
              data-testid="login-error"
            >
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{err}</span>
            </div>
          ) : null}

          <div>
            <label htmlFor="identifier" className="block text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)] mb-1.5">
              Username or Email
            </label>
            <input
              id="identifier"
              type="text"
              autoComplete="username"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              className="sc-input"
              data-testid="login-identifier"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)] mb-1.5">
              Password
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="sc-input"
              data-testid="login-password"
            />
            <div className="mt-1.5 text-right">
              <Link
                to="/forgot-password"
                className="text-[11px] text-[var(--sc-text-mute)] hover:text-[#FFC20E] uppercase tracking-[0.18em]"
                data-testid="login-forgot-password"
              >
                Forgot password?
              </Link>
            </div>
          </div>

          <button type="submit" disabled={busy} className="sc-btn-white w-full" style={{ padding: '14px 22px', fontSize: 13 }} data-testid="login-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />}
            Sign in
          </button>

          {captchaRequired && captchaSiteKey ? (
            <div className="pt-2">
              <TurnstileWidget siteKey={captchaSiteKey} onVerified={onCaptchaVerified} />
            </div>
          ) : null}

          <div className="text-center text-[12px] text-[var(--sc-text-dim)]">
            Don’t have an account?{' '}
            <Link
              to={refQS ? `/signup?ref=${encodeURIComponent(refQS)}` : '/signup'}
              className="text-[#FFC20E] font-semibold hover:underline"
              data-testid="login-to-signup"
            >
              Create one
            </Link>
          </div>
        </form>
      </div>

      <EarningsDisclaimer variant="compact" />
    </main>
  );
};

export default LoginPage;
